import React, { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import { Mail, ArrowLeft, CheckCircle2, AlertCircle, Lock, Loader } from "lucide-react";

const ForgotPassword = () => {
  const [email, setEmail] = useState("");         
  const [message, setMessage] = useState("");     
  const [messageType, setMessageType] = useState("success");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const navigate = useNavigate();
  

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage("");

    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}users/custom-password-reset/`,
        { email },
      );

      setSubmitted(true);
      setMessage(
        "Password reset link sent! Check your email inbox and spam folder for further instructions."
      );
      setMessageType("success");
      setEmail("");
    } catch (error) {
      console.error("Password reset error:", error);
      setMessage(
        "If an account exists with this email, you'll receive a password reset link shortly. Please check your inbox."
      );
      setMessageType("info");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center px-4 py-12 relative overflow-hidden">
      <Helmet>
        <title>Forgot Password | Governance & Continuity Assessment Tool</title>
        <meta name="description" content="Reset your password to regain access to your account." />
      </Helmet>

      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#FF4D00] rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-pulse"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-orange-500 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-pulse" style={{ animationDelay: "2s" }}></div>
      </div>

      <div className="w-full max-w-md relative z-10">
        {/* Back Button */}
        <button
          onClick={() => navigate("/login")}
          className="flex items-center gap-2 text-gray-600 hover:text-[#FF4D00] font-medium mb-8 transition"
        >
          <ArrowLeft size={20} />
          Back to Login
        </button>

        {/* Logo/Brand */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-[#FF4D00] to-orange-600 rounded-2xl mb-4 shadow-lg">
            <Lock className="text-white" size={32} />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Reset Password</h1>
          <p className="text-gray-600">We'll help you get back into your account</p>
        </div>

        {/* Main Card */}
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden border border-gray-200">
          {/* Header */}
          <div className="bg-gradient-to-r from-[#FF4D00] to-orange-600 px-8 py-6">
            <p className="text-orange-100 text-sm font-medium">
              Secure Password Reset
            </p>
          </div>

          {/* Form Content */}
          <div className="p-8 sm:p-10">
            {/* Success Message */}
            {submitted && messageType === "success" && (
              <div className="mb-8 p-6 bg-green-50 border border-green-200 rounded-xl">
                <div className="flex items-start gap-4">
                  <CheckCircle2 className="text-green-600 flex-shrink-0 mt-0.5" size={24} />
                  <div>
                    <h3 className="font-bold text-green-900 mb-1">Check Your Email</h3>
                    <p className="text-green-800 text-sm">
                      {message}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Info Message */}
            {!submitted && messageType === "info" && message && (
              <div className="mb-8 p-4 bg-blue-50 border border-blue-200 rounded-xl flex items-start gap-3">
                <AlertCircle className="text-blue-600 flex-shrink-0 mt-0.5" size={20} />
                <p className="text-blue-800 text-sm">{message}</p>
              </div>
            )}

            {!submitted ? (
              <>
                <p className="text-gray-600 text-sm mb-6 leading-relaxed">
                  Enter the email address associated with your account, and we'll send you a link to reset your password.
                </p>

                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Email Field */}
                  <div>
                    <label htmlFor="email" className="block text-sm font-semibold text-gray-900 mb-2">
                      Email Address
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                      <input
                        type="email"
                        id="email"
                        required
                        disabled={loading}
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="you@example.com"
                        className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF4D00] focus:border-transparent transition disabled:bg-gray-100 disabled:cursor-not-allowed"
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      We'll never share your email with anyone else.
                    </p>
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={loading || !email}
                    className="w-full bg-gradient-to-r from-[#FF4D00] to-orange-600 text-white py-3 rounded-lg font-semibold hover:shadow-lg transform hover:scale-105 transition duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 flex items-center justify-center gap-2"
                  >
                    {loading ? (
                      <>
                        <Loader className="animate-spin" size={20} />
                        Sending...
                      </>
                    ) : (
                      <>
                        Send Reset Link
                      </>
                    )}
                  </button>
                </form>

                {/* Divider */}
                <div className="relative my-8">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-300"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-2 bg-white text-gray-500">Or</span>
                  </div>
                </div>

                {/* Help Links */}
                <div className="space-y-3">
                  <button
                    onClick={() => navigate("/login")}
                    type="button"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition text-sm font-medium text-gray-700"
                  >
                    Back to Login
                  </button>
                  <button
                    onClick={() => navigate("/contact")}
                    type="button"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition text-sm font-medium text-gray-700"
                  >
                    Contact Support
                  </button>
                </div>
              </>
            ) : (
              <>
                {/* Success State Content */}
                <div className="text-center space-y-6">
                  <div className="space-y-2">
                    <p className="text-gray-700 text-sm leading-relaxed">
                      A password reset link has been sent to your email. The link will expire in 24 hours.
                    </p>
                    <p className="text-gray-600 text-sm">
                      Don't see it? Check your spam folder or request a new link.
                    </p>
                  </div>

                  <div className="pt-6 border-t border-gray-200 space-y-3">
                    <button
                      onClick={() => setSubmitted(false)}
                      type="button"
                      className="w-full px-4 py-3 bg-gradient-to-r from-[#FF4D00] to-orange-600 text-white rounded-lg font-semibold hover:shadow-lg transition"
                    >
                      Try Another Email
                    </button>
                    <button
                      onClick={() => navigate("/login")}
                      type="button"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition text-sm font-medium text-gray-700"
                    >
                      Back to Login
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-8 py-6 border-t border-gray-200 text-center text-xs text-gray-500">
            <p>
              Remember your password?{" "}
              <button
                onClick={() => navigate("/login")}
                className="text-[#FF4D00] hover:underline font-medium"
              >
                Sign in here
              </button>
            </p>
          </div>
        </div>

        {/* Security Tips */}
        <div className="mt-8 p-6 bg-white rounded-xl border border-gray-200 shadow">
          <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <AlertCircle size={20} className="text-[#FF4D00]" />
            Security Tips
          </h3>
          <ul className="space-y-2 text-sm text-gray-600">
            <li className="flex items-start gap-2">
              <span className="text-[#FF4D00] font-bold">•</span>
              <span>Never share your password reset link with anyone</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-[#FF4D00] font-bold">•</span>
              <span>Reset links expire after 24 hours</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-[#FF4D00] font-bold">•</span>
              <span>Use a strong, unique password</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;